import React, {useEffect, useMemo, useRef} from 'react';
import {useAuth} from '../../auth/hooks/useAuth';
import {AuthorizationService} from '../../../Services/authorization/AuthorizationService';
import {Navigate} from 'react-router-dom';
import Spinner from '../../../Presentation/components/ui/Spinner';
import {useServiceProviderControl} from '../../serviceProviders/hooks/useServiceProviderControl';
import {mapMyBrandsResponseToProviderData} from '../../serviceProviders/utils/providerDataMapper';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

interface ProviderDashboardLayoutProps {
  children: React.ReactNode;
}

export default function ProviderDashboardLayout ({children}: ProviderDashboardLayoutProps) {
  const {isAuthenticated, loading, initialized, user, updateUserProviderData} = useAuth();
  const {getMyBrands} = useServiceProviderControl();
  const authorizationService = useMemo(() => AuthorizationService.getInstance(), []);
  const isProvider = authorizationService.isServiceProvider(user);
  const hasFetchedProviderBrands = useRef<boolean>(false);
  const existingBrandNames = user?.getAssignedBrandNames
    ? user.getAssignedBrandNames()
    : (user as any)?.providerData?.assignedBrands?.map((b: any) => b.name) || [];

  useEffect(() => {
    let isMounted = true;

    const shouldFetchBrands =
      isProvider &&
      user?.id &&
      (!user.providerData || user.providerData.assignedBrands.length === 0);

    if (!shouldFetchBrands || hasFetchedProviderBrands.current) {
      return;
    }

    hasFetchedProviderBrands.current = true;

    const fetchProviderBrands = async () => {
      try {
        const response = await getMyBrands();
        if (!isMounted) {
          return;
        }
        const providerData = mapMyBrandsResponseToProviderData(response, user.id);
        updateUserProviderData(providerData);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        StandardizedErrorHandler.getInstance().handleError(error);
      }
    };

    fetchProviderBrands();

    return () => {
      isMounted = false;
    };
  }, [getMyBrands, isProvider, updateUserProviderData, user]);

  // Debug: Log authentication state removed for production

  // Wait for authentication to initialize
  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner center size={40} />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to unauthorized if not a provider
  if (!isProvider) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Provider Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
              <span className="text-brand-600 font-semibold text-lg">👤</span>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                مزود الخدمة: {user?.username || ''}
              </h1>
              <p className="text-sm text-gray-600">
                البراندات المرتبطة: {existingBrandNames.length > 0 ? existingBrandNames.join(', ') : 'لا توجد براندات مرتبطة'}
              </p>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={() => {
              // TODO: Implement logout
              window.location.href = '/login';
            }}
            className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
          >
            تسجيل الخروج
          </button>
        </div>
      </div>

      {/* Main Content - Only Map */}
      <div className="flex-1">
        {children}
      </div>
    </div>
  );
}
